import { AlertTriangle, Clock, Database, FileSpreadsheet, Loader2, Play } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { RunExtractionOptions } from '@/features/documents/hooks/useDocumentList'
import { api } from '@/services/api'
import type { DocumentMapping } from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'
import {
  computeExtractionEstimate,
  type EstimateInputItem,
  type EstimateResult,
  formatEstimateRange,
} from '@/utils/extraction-estimate'
import { type ResolvableSchema, resolveExtractionSchema } from '@/utils/extraction-schema'

/** A selected document that structured ingest already has a mapping for. */
export type StructuredSelection = DocumentMapping & { id: string }

interface ExtractionConfigModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId?: string
  documentCount: number
  pipelineName: string
  selectedDocuments?: EstimateInputItem[]
  structuredDocuments?: StructuredSelection[]
  onReviewMapping?: (analysisId: string) => void
  onRun: (
    schemaName?: string,
    userPrompt?: string,
    schemaVersion?: number,
    options?: RunExtractionOptions
  ) => void
  isRunning: boolean
}

export function ExtractionConfigModal({
  open,
  onOpenChange,
  caseId,
  documentCount,
  pipelineName,
  selectedDocuments,
  structuredDocuments = [],
  onReviewMapping,
  onRun,
  isRunning,
}: ExtractionConfigModalProps) {
  const [schemas, setSchemas] = useState<ResolvableSchema[]>([])
  // The case's bound schema id (null ⇒ follow the global active). Selection now
  // happens only on the Case Schema page — here it is read-only (slice 08).
  const [boundSchemaId, setBoundSchemaId] = useState<string | null>(null)
  const [userPrompt, setUserPrompt] = useState('')
  const [loadingSchemas, setLoadingSchemas] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoadingSchemas(true)

    // Case mode reads the case-scoped union list so a binding to this case's own
    // private schema resolves for display; admin/global falls back to the global list.
    const fetchSchemas = caseId ? api.schemas.listForCase(caseId) : api.cases.getExtractionSchemas()
    const fetchSettings = caseId ? api.cases.getCaseSettings(caseId) : Promise.resolve(null)

    Promise.all([fetchSchemas, fetchSettings])
      .then(([data, settings]) => {
        setSchemas(
          data.map(s => ({ id: s.id, name: s.name, version: s.version, is_active: s.is_active }))
        )
        setBoundSchemaId(settings?.schema_id ?? null)
      })
      .catch(() => setSchemas([]))
      .finally(() => setLoadingSchemas(false))
  }, [open, caseId])

  // Reset prompt and the structured-ingest choice when modal closes
  useEffect(() => {
    if (!open) {
      setUserPrompt('')
    }
  }, [open])

  const resolved = useMemo(
    () => resolveExtractionSchema(schemas, boundSchemaId),
    [schemas, boundSchemaId]
  )
  // Following the workspace default when there is no binding (or it no longer
  // resolves to a known schema); otherwise pinned to the case's explicit choice.
  const following = !boundSchemaId || resolved?.id !== boundSchemaId

  const estimate = useMemo<EstimateResult>(
    () => computeExtractionEstimate(selectedDocuments ?? []),
    [selectedDocuments]
  )

  const unconfirmed = structuredDocuments.filter(document => document.confirmed !== true)
  const excludedCount = unconfirmed.length
  const runCount = Math.max(documentCount - excludedCount, 0)

  const handleRun = () => {
    onRun(resolved?.name || undefined, userPrompt.trim() || undefined, resolved?.version, {
      excludeDocumentIds: unconfirmed.map(d => d.id),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl p-6 backdrop-blur-xl backdrop-saturate-150">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg font-semibold tracking-tight">Run Extraction</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Schema</Label>
            {loadingSchemas ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Loading schema...
              </div>
            ) : resolved ? (
              <div className="rounded-xl border border-border/40 bg-muted/40 p-3">
                <div className="flex items-center gap-2">
                  <Database className="size-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium text-foreground">
                    {resolved.name}{' '}
                    <span className="text-muted-foreground tabular-nums">v{resolved.version}</span>
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {following ? 'Workspace-default schema' : "This case's schema"}
                  {caseId && ' · manage it on the Case Schema page'}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-warning-border bg-warning-surface p-3 text-xs text-warning-foreground">
                No schema available — define one on the Case Schema page before running extraction.
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Instructions for analyst <span className="lowercase">·</span> optional
            </Label>
            <Textarea
              value={userPrompt}
              onChange={e => setUserPrompt(e.target.value)}
              placeholder="e.g. Focus on financial transactions and BTS records..."
              rows={3}
              className="rounded-xl resize-none"
            />
          </div>

          <StructuredNotice documents={structuredDocuments} onReview={onReviewMapping} />

          <EstimatePanel estimate={estimate} />

          <div className="text-sm text-muted-foreground">
            {runCount} {runCount === 1 ? 'document' : 'documents'} to run
            {excludedCount > 0 && ` (${excludedCount} left to structured ingest)`}
            <span className="mx-1.5">&middot;</span>
            {pipelineName}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={() => onOpenChange(false)}
            disabled={isRunning}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            className="rounded-xl gap-2"
            onClick={handleRun}
            disabled={isRunning || loadingSchemas || !resolved || runCount === 0}
          >
            {isRunning ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="size-4" />
                Run extraction
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Estimate panel
// ---------------------------------------------------------------------------

function EstimatePanel({ estimate }: { estimate: EstimateResult }) {
  if (!estimate.show_panel) {
    if (estimate.tier === 'none' && estimate.item_count > 0 && estimate.max_sec === 0) {
      // Tabular-only — no estimate available; render nothing.
      return null
    }
    if (estimate.tier === 'none' && estimate.max_sec < 60 && estimate.max_sec > 0) {
      return (
        <div className="text-xs text-muted-foreground">
          Estimated processing time: <span className="font-medium">~under a minute</span>
        </div>
      )
    }
    return null
  }

  // Static placeholder branch (PDF/DOCX/IMAGE without precise computation)
  if (estimate.static_placeholder) {
    return (
      <div className="rounded-xl border border-border/40 bg-muted/40 p-4">
        <div className="flex items-start gap-3">
          <Clock className="size-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Estimated processing time
            </div>
            <div className="text-sm font-medium">{estimate.static_placeholder}</div>
          </div>
        </div>
      </div>
    )
  }

  // Numeric estimate — pick visual treatment by tier.
  const range = formatEstimateRange(estimate.min_sec, estimate.max_sec)

  if (estimate.tier === 'red') {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-destructive font-medium">
              Very large file — consider splitting
            </div>
            <div className="text-sm font-semibold">{range}</div>
            <div className="text-xs text-muted-foreground">
              {estimate.chunk_count} chunks. Tip: split this log into smaller files (e.g. by year)
              for parallel processing.
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (estimate.tier === 'amber') {
    return (
      <div className="rounded-xl border border-warning-border bg-warning-surface p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="size-5 text-warning shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-warning-foreground font-medium">
              Large log — this may take a while
            </div>
            <div className="text-sm font-semibold">{range}</div>
            <div className="text-xs text-muted-foreground">
              {estimate.chunk_count} chunks. You can close this dialog and check status later.
            </div>
          </div>
        </div>
      </div>
    )
  }

  // info tier
  return (
    <div className="rounded-xl border border-border/40 bg-muted/40 p-4">
      <div className="flex items-start gap-3">
        <Clock className={cn('size-5 shrink-0 mt-0.5', 'text-muted-foreground')} />
        <div className="space-y-0.5">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Estimated processing time
          </div>
          <div className="text-sm font-medium">{range}</div>
          <div className="text-xs text-muted-foreground">
            {estimate.chunk_count} {estimate.chunk_count === 1 ? 'chunk' : 'chunks'} · estimated
            from file size
          </div>
        </div>
      </div>
    </div>
  )
}

function StructuredNotice({
  documents,
  onReview,
}: {
  documents: StructuredSelection[]
  onReview?: (analysisId: string) => void
}) {
  if (documents.length === 0) return null
  return (
    <div className="space-y-2 rounded-xl border border-border/40 bg-muted/40 p-3">
      <div className="flex items-start gap-2">
        <FileSpreadsheet className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          Confirmed tables run through their structured mapping. Tables awaiting confirmation are
          excluded until you review their mapping.
        </p>
      </div>
      <ul className="space-y-1 text-xs">
        {documents.map(document => (
          <li key={document.id} className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-foreground">{document.filename}</span>
            <span className="text-muted-foreground">
              {document.confirmed ? 'Ready for structured extraction' : 'Confirm mapping first'}
            </span>
            {onReview && (
              <Button variant="link" size="sm" onClick={() => onReview(document.analysisId)}>
                Review mapping
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
